import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Search, Trash2 } from 'lucide-react';
import Footer from '../Footer.js';
import { Card, CardContent, Input, Button, Dialog, DialogContent, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import dayjs from 'dayjs';
import WatchListTabs from "./WatchListTabs.jsx";
import {
  clearWatchlist,
  fetchWatchlistRequest,
  setActiveTab,
  deleteWatchlistRequest,
  setSelectedItem,
  clearSelectedItem
} from '../../store/reducers/watchlistReducer.js';

import MarketDetailDialog from "../MarketDetailDialog.jsx";
import ConfirmationDialog from "../ConfirmationDialog.js";

function WatchList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formattedDate, setFormattedDate] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Redux state
  const {
    activeTab,
    loading: watchlistLoading,
    marketWatchListData,
    selectedItem,
  } = useSelector(state => state.watchlist);

  const [openDialog, setOpenDialog] = useState(false);

  const handleSearchBar = () => {
    navigate("/search-bar");
  };

  const handleDeleteClick = (e, item) => {
    e.stopPropagation();
    setItemToDelete(item.watchlist_id);
    setShowDeleteConfirm(true);
  };
  const handleDeleteConfirm = () => {

    if (itemToDelete) {
      dispatch(deleteWatchlistRequest({ itemToDelete }));
    }
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const extractDateFromSymbol = (tradingSymbol) => {
    const match = tradingSymbol.match(/\d{1,2}[A-Z]{3}\d{2}/);
    return match ? match[0] : null;
  };

  useEffect(() => {
    if (selectedItem !== null) {
      let formattedDate = dayjs(extractDateFromSymbol(selectedItem.tradingSymbol)).format('YYYY-MM-DD');
      setFormattedDate(formattedDate);

    }
  }, [dispatch, selectedItem])

  if (openDialog && selectedItem) {
    const updatedItem = marketWatchListData?.find((item) => item.tradingSymbol === selectedItem.tradingSymbol);
    if (updatedItem) {
      dispatch(setSelectedItem(updatedItem));
    }
  }

  // Open Dialog
  const handleCardClick = (item) => {
    dispatch(setSelectedItem(item));
    setOpenDialog(true);
  };

  // Close Dialog
  const handleDialogClose = () => {
    setOpenDialog(false);
    dispatch(clearSelectedItem());
  };

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      dispatch({ type: 'START_MARKET_DATA_UPDATES_WATCHLIST' });
    }

    return () => {
      isMounted = false;
      dispatch({ type: 'STOP_MARKET_DATA_UPDATES_WATCHLIST' });
      dispatch(clearWatchlist());
    };
  }, [dispatch]);

  const isCalled = useRef(false);

  useEffect(() => {
    if (!isCalled.current) {
      dispatch(fetchWatchlistRequest());
      isCalled.current = true; // mark as called
    }
    // Cleanup: stop market data updates when component unmounts
    // return () => {
    //   dispatch(stopMarketDataUpdates());
    // };
  }, []);


  const removeNumbersAndAfter = (tradingSymbol) => {
    if (!tradingSymbol) return '';
    const index = tradingSymbol.search(/\d/);
    return index !== -1 ? tradingSymbol.substring(0, index) : tradingSymbol;
  };

  const abbreviateNumber = (number) => {
    if (!number || isNaN(number)) return "--";
    if (number >= 1e9) return (number / 1e9).toFixed(1) + 'B';
    if (number >= 1e6) return (number / 1e6).toFixed(1) + 'M';
    if (number >= 1e3) return (number / 1e3).toFixed(1) + 'K';
    return number.toString();
  };
  const handleRedirect = (buttonType, tradingSymbol, exchange, price, lots, netChange, percentChange, symbolToken, lowerCircuit, upperCircuit, high, low, script_id, exchange_id, expiry) => {

    navigate('/add-order', {
      state: {
        m_buttonType: buttonType,
        m_scriptName: tradingSymbol,
        m_exchange: exchange,
        m_price: price,
        m_lotssize: 1,
        m_qty: lots,
        m_netChange: netChange,
        m_percentChange: percentChange,
        m_symbolToken: symbolToken,
        m_activeTabValue: activeTab,
        m_expiry: expiry,
        m_lowerCircuit: lowerCircuit,
        m_upperCircuit: upperCircuit,
        m_high: high,
        m_low: low,
        m_script_id: script_id,
        m_exchange_id: exchange_id
      },
    });
  };

  const getChangeColor = (change) => {
    if (change > 0) return "text-green-500";
    if (change < 0) return "text-red-500";
    return "";
  };

  const safeWatchlist = Array.isArray(marketWatchListData)
    ? marketWatchListData
    : [];

  const filteredOrders = safeWatchlist?.filter((order) => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();

    return (
      order.tradingSymbol?.toLowerCase().includes(query) ||
      String(order.lots).includes(query) ||
      String(order.qty).includes(query)
    );
  });
  return (
    <div className="flex items-center justify-center flex-col bg-black min-h-screen p-0">
      <div className="flex-1 overflow-hidden p-4 max-w-sm min-w-[370px] w-full bg-gray-100">

        <div className="flex items-center justify-between w-full p-4">
          <div className="">
            <h3 className="text-[1rem] font-lato font-semibold">WatchList</h3>
          </div>
          <div className="flex items-center -mr-[15px]">
            <Input
              readOnly
              placeholder="Search"
              endAdornment={<Search sx={{ bottom: "13px", right: "13px", fontSize: "12px" }} />}
              disableUnderline
              onClick={handleSearchBar}
              sx={{
                width: "90%",
                padding: "10px 13px 10px 30px",
                backgroundColor: "white",
                borderRadius: "50px",
                lineHeight: "18px",
                height: "25%",
                fontSize: "12px",
              }}
            />
          </div>
        </div>
        <div className="flex items-center w-full mt-2">
          <div className="flex items-center w-full" >
            <Input
              endAdornment={<Search sx={{ bottom: '13px', right: '13px', fontSize: '12px' }} />}
              disableUnderline
              placeholder="Search by symbol, lots or qty"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}

              sx={{
                width: '100%',
                padding: '10px 13px 10px 30px',
                backgroundColor: 'white',
                borderRadius: '50px',
                lineHeight: '18px',
                height: '25%',
                fontSize: '12px',
              }}
            />
          </div>
        </div>
        <div className="mt-4">
          <WatchListTabs />

          <div
            className="overflow-y-auto pb-20 [&::-webkit-scrollbar]:w-1 
                [&::-webkit-scrollbar-track]:bg-transparent 
                [&::-webkit-scrollbar-thumb]:bg-transparent 
                scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent"
            style={{ maxHeight: "calc(100vh - 160px)" }}
          >
            <div className="mb-2">
              {filteredOrders?.length > 0 ? (
                filteredOrders.map((item, index) => (
                  <Card
                    className="px-0 py-1 mt-2 h-auto bg-white cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleCardClick(item)}
                    key={index}
                  >
                    <CardContent className="p-3">
                      {/* Top Row: Symbol, Delete, Bid/Ask */}
                      <div className="flex items-center justify-between mb-2 relative">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div>
                              <h3 className="font-semibold text-xs text-gray-900 leading-tight">
                                {item.tradingSymbol}
                              </h3>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {item.expiry ? dayjs(item.expiry).format("DD-MMM-YYYY").toUpperCase() : 'N/A'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Bid/Ask Prices */}
                        <div className="flex gap-2 mr-7" >
                          <div className="bg-red-100 border border-red-200 rounded px-3 py-1 min-w-[60px] text-center w-16">
                            <span className="text-sm font-semibold text-red-700"
                              onClick={() =>
                                handleRedirect(
                                  'sell',
                                  item?.tradingSymbol,
                                  item?.exchange,
                                  item?.depth?.buy[0]?.price,
                                  item?.lots,
                                  item?.netChange,
                                  item?.percentChange,
                                  item?.symbolToken,
                                  item?.lowerCircuit,
                                  item?.upperCircuit,
                                  item?.high,
                                  item?.low,
                                  item?.script_id,
                                  item?.exchange_id,
                                  formattedDate
                                )
                              }>
                              {Number(item.depth?.buy?.[0]?.price ?? 0)}
                            </span>
                          </div>
                          <div className="bg-green-100 border border-green-200 rounded px-3 py-1 min-w-[60px] text-center w-16">
                            <span className="text-sm font-semibold text-green-700 "
                              onClick={() =>
                                handleRedirect(
                                  'buy',
                                  item?.tradingSymbol,
                                  item?.exchange,
                                  item?.depth?.sell[0]?.price,
                                  item?.lots,
                                  item?.netChange,
                                  item?.percentChange,
                                  item?.symbolToken,
                                  item?.lowerCircuit,
                                  item?.upperCircuit,
                                  item?.high,
                                  item?.low,
                                  item?.script_id,
                                  item?.exchange_id,
                                  formattedDate
                                )
                              }
                            >
                              {Number(item.depth?.sell?.[0]?.price ?? 0)}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDeleteClick(e, item)}
                          className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all duration-200 mr-2 absolute top-[-9px] right-[-16px]"
                          title="Delete from watchlist"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Bottom Row: Trading Data */}
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex-1">
                          <span className="text-gray-700">
                            Change: <span className={getChangeColor(item?.netChange || 0)}>
                              {Number(item?.netChange ?? 0).toFixed(2)}
                              {` (${Number(item?.percentChange ?? 0).toFixed(2)}%)`}
                            </span>
                          </span>
                        </div>

                        <div className="flex gap-4 text-gray-600">
                          <span>
                            LTP: <span className="text-gray-800 font-medium">
                              {Number(item?.ltp ?? 0).toFixed(2)}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs mt-1">
                        <div className="flex gap-4 text-gray-600">
                          <span>
                            H: <span className="text-gray-800 font-medium">
                              {Number(item?.high ?? 0).toFixed(2)}
                            </span>
                          </span>
                        </div>
                        <div className="flex gap-4 text-gray-600">
                          <span>
                            L: <span className="text-gray-800 font-medium">
                              {Number(item?.low ?? 0).toFixed(2)}
                            </span>
                          </span>
                        </div>
                        <div className="flex gap-4 text-gray-600">
                          <span>
                            Open: <span className="text-gray-800 font-medium">
                              {Number(item?.open ?? 0).toFixed(2)}
                            </span>
                          </span>
                        </div>
                        <div className="flex gap-4 text-gray-600">
                          <span>
                            Close: <span className="text-gray-800 font-medium">
                              {Number(item?.close ?? 0).toFixed(2)}
                            </span>
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : watchlistLoading ? (
                // Shimmer placeholders shaped like the watchlist cards.
                Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="px-3 py-3 mt-2 h-auto bg-white rounded-lg shadow-sm animate-pulse"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="space-y-2">
                        <div className="h-3 w-32 bg-gray-300 rounded"></div>
                        <div className="h-2 w-20 bg-gray-200 rounded"></div>
                      </div>
                      <div className="flex gap-2">
                        <div className="h-7 w-16 bg-gray-200 rounded"></div>
                        <div className="h-7 w-16 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="h-2 w-24 bg-gray-200 rounded"></div>
                      <div className="h-2 w-16 bg-gray-200 rounded"></div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="h-2 w-12 bg-gray-200 rounded"></div>
                      <div className="h-2 w-12 bg-gray-200 rounded"></div>
                      <div className="h-2 w-12 bg-gray-200 rounded"></div>
                      <div className="h-2 w-12 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center h-[350px] w-full">
                  <div className="w-32 h-32 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                    <span className="text-4xl text-gray-400">📊</span>
                  </div>
                  <p className="text-gray-500 text-center">No items in watchlist for {activeTab}.</p>
                </div>
              )}
            </div>
          </div>
          <MarketDetailDialog
            open={openDialog}
            onClose={handleDialogClose}
            selectedItem={selectedItem}
            formattedDate={formattedDate}
            handleRedirect={handleRedirect}
          />

          {/* Delete Confirmation Dialog */}
          <ConfirmationDialog
            isOpen={showDeleteConfirm}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            title="Delete from Watchlist"
            message={`Are you sure you want to remove "${itemToDelete?.script_name || 'this item'}" from your watchlist?`}
            confirmText="Delete"
            cancelText="Cancel"
            type="danger"
          />
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default WatchList;